import { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { ButtonUI } from 'sonata-react-components';

import { sharedCountry } from '../../domain/cartographyGrouping';
import { canConfirmNewGroup } from '../../domain/groupRules';
import { useGrouping } from '../../hooks/useGrouping';
import { useGroupingItems } from '../../hooks/useGroupingItems';
import { CartographyLevelSelector } from '../CartographyLevelSelector/CartographyLevelSelector';
import { DEFAULT_GROUP_COLOR, GroupForm } from '../GroupForm/GroupForm';
import { GroupingModal } from '../GroupingModal/GroupingModal';
import { GroupingModeSelector } from '../GroupingModeSelector/GroupingModeSelector';
import { groupingMessages } from '../groupingMessages';
import { SelectedItemsSummary } from '../SelectedItemsSummary/SelectedItemsSummary';
import { useCartographicNewGroup } from './useCartographicNewGroup';

import type { GroupFamily } from '../../domain/grouping.types';
import type { GroupingMode } from '../GroupingModeSelector/GroupingModeSelector';
import type { GroupingNotice } from '../groupingNotice.types';

export interface NewGroupModalProps {
  family: GroupFamily;
  /** UI ids of the selected items that will form the group. */
  itemIds: string[];
  onApplied: (notice: GroupingNotice) => void;
  onClose: () => void;
}

/**
 * "New group" for the currently selected items: name + color (custom), or, when every selected screen / venue is in
 * the same country, one group per area of a cartographic level.
 */
export function NewGroupModal({ family, itemIds, onApplied, onClose }: Readonly<NewGroupModalProps>) {
  const { formatMessage } = useIntl();
  const grouping = useGrouping(family);
  const items = useGroupingItems(family);
  const [name, setName] = useState('');
  const [color, setColor] = useState(DEFAULT_GROUP_COLOR);
  const [mode, setMode] = useState<GroupingMode>('custom');

  const selectedItems = useMemo(() => items.filter(item => itemIds.includes(item.id)), [items, itemIds]);
  const selectedItemIds = selectedItems.map(item => item.id);
  const country = useMemo(() => sharedCountry(family, selectedItems), [family, selectedItems]);
  const isCartography = mode === 'cartography' && country !== null;
  const cartographic = useCartographicNewGroup({
    applyGroupChange: grouping.applyGroupChange,
    country,
    enabled: isCartography,
    items: selectedItems,
    onApplied
  });

  // Fetching levels / venue tags or creating the groups: confirm shows a spinner (ButtonUI ignores clicks meanwhile)
  const busy = isCartography && (cartographic.loading || cartographic.submitting);
  const submitting = isCartography && cartographic.submitting;
  const canConfirm = isCartography
    ? cartographic.canConfirm
    : canConfirmNewGroup({ color, groups: grouping.inUseGroups, name, selectedItemIds });

  const handleConfirm = () => {
    if (isCartography) {
      cartographic.confirm();
      return;
    }
    const groupName = name.trim();
    const { affectedItemIds } = grouping.applyGroupChange({
      color,
      itemIds: selectedItemIds,
      name: groupName,
      type: 'create'
    });
    onApplied({ affectedItemIds, groupName, itemCount: selectedItemIds.length, kind: 'created' });
  };

  return (
    <GroupingModal
      closeDisabled={submitting}
      id="newGroupModal"
      leftButton={
        <ButtonUI
          disabled={submitting}
          id="newGroupModal-cancel"
          onClick={onClose}
          shape="squareRounded"
          text={formatMessage(groupingMessages.cancel)}
          type="secondary"
        />
      }
      onClose={onClose}
      rightButton={
        <ButtonUI
          disabled={!canConfirm && !busy}
          id="newGroupModal-confirm"
          loading={busy}
          onClick={handleConfirm}
          shape="squareRounded"
          text={formatMessage(groupingMessages.confirm)}
          type="primary"
        />
      }
      title={formatMessage(groupingMessages.newGroupTitle)}
    >
      <SelectedItemsSummary family={family} groups={grouping.groups} items={selectedItems} />
      {country && <GroupingModeSelector id="newGroupModal" mode={mode} onChange={setMode} />}
      {isCartography ? (
        <CartographyLevelSelector
          country={country}
          level={cartographic.level}
          levels={cartographic.levels}
          loading={cartographic.loading}
          onChange={cartographic.setLevel}
        />
      ) : (
        <GroupForm
          color={color}
          id="newGroupModal"
          name={name}
          nameError={grouping.validateName(name)}
          onColorChange={setColor}
          onNameChange={setName}
        />
      )}
    </GroupingModal>
  );
}
